(function () {
    'use strict';

    const PaginationManager = {
        config: {
            SELECTORS: {
                articleListing: '.articleListing',
                resultsContainer: '.article-listing-results',
                articleCard: '.col-12, .col-md-6, .col-lg-4, .card'
            },
            get ENDPOINT() {
                if (typeof SiteInfo !== 'undefined' && SiteInfo.Site) {
                    const protocol = window.location.protocol;
                    const domain = SiteInfo.Site.Domain || window.location.host;
                    let path = SiteInfo.Site.Path || '';
                    if (path.endsWith('/')) path = path.slice(0, -1);
                    return `${protocol}//${domain}${path}/api/article-listing/page`;
                }
                return '/api/article-listing/page';
            }
        },

        instances: [],

        init() {
            this.getListings().forEach((listing, index) => {
                this.initializeListing(listing, index);
            });
        },

        getListings() {
            return Array.from(document.querySelectorAll(this.config.SELECTORS.articleListing));
        },

        initializeListing(listingElement, listingIndex) {
            const isRelated = listingElement.getAttribute('data-is-related') === 'true';

            // Skip AJAX pagination if it's related articles (no payload for composed articles)
            if (isRelated) return;

            const payloadRaw = listingElement.getAttribute('data-payload');
            if (!payloadRaw) return;

            let payload;
            try {
                // Try decoding HTML entities and parsing JSON
                const decoded = new DOMParser().parseFromString(payloadRaw, "text/html").documentElement.textContent;
                payload = JSON.parse(decoded);
            } catch (e) {
                console.error("Failed to parse pagination payload", e);
                return;
            }

            let initialExcludeIds = [];
            try {
                const raw = listingElement.getAttribute('data-exclude-ids');
                if (raw) {
                    const decoded = new DOMParser().parseFromString(raw, "text/html").documentElement.textContent;
                    initialExcludeIds = JSON.parse(decoded);
                }
            } catch (e) {
                initialExcludeIds = [];
            }

            const displayOption = listingElement.getAttribute('data-display-option') || 'standard';
            const pageSize = parseInt(listingElement.getAttribute('data-page-size'), 10) || 12;
            const resultsContainer = listingElement.querySelector(this.config.SELECTORS.resultsContainer);
            const totalPages = parseInt(listingElement.getAttribute('data-total-pages'), 10) || 1;

            // Count initial articles to see if we might have a next page
            let initialCount = 0;
            if (resultsContainer) {
                // Approximate article count based on column/card layout selectors
                initialCount = resultsContainer.querySelectorAll('.card').length;
            }


            // If we only have 1 page or fewer, or fewer items than page size on first load, no pagination is needed
            if (totalPages <= 1 || initialCount < pageSize) {
                return;
            }

            const instanceId = `pagination-instance-${listingIndex}-${Date.now()}`;
            const instance = {
                id: instanceId,
                listingElement,
                resultsContainer,
                payload,
                displayOption,
                pageSize,
                totalPages,
                currentPage: 1,
                hasNext: totalPages > 1,
                usedIds: new Set(initialExcludeIds)
            };

            this.instances.push(instance);
            listingElement.setAttribute('data-pagination-id', instanceId);

            this.createPaginationControls(instance);
            this.updatePaginationControls(instanceId, 1, true);
        },

        async fetchPage(instanceId, pageNumber, skipScroll = false) {
            const instance = this.instances.find(i => i.id === instanceId);
            if (!instance) return;

            // Calculate 'From' based on pageNumber (1-indexed)
            const from = (pageNumber - 1) * instance.pageSize;

            const otherWidgetsExcludeIds = this.instances
                .filter(i => i.id !== instanceId)
                .flatMap(i => Array.from(i.usedIds));

            const mergedExcludeIds = [
                ...new Set([
                    ...(instance.payload.excludeIds || []),
                    ...otherWidgetsExcludeIds
                ])
            ];

            const requestBody = {
                displayOption: instance.displayOption,
                payload: {
                    ...instance.payload,
                    from: from,
                    size: instance.pageSize,
                    excludeIds: mergedExcludeIds
                }
            };

            if (instance.resultsContainer) {
                instance.resultsContainer.style.opacity = '0.5';
                instance.resultsContainer.style.position = 'relative';

                let loader = document.createElement('div');
                loader.className = 'pagination-loader-overlay';
                loader.innerHTML = '<div class="spinner-border text-primary" style="width: 3rem; height: 3rem;" role="status"><span class="visually-hidden">Loading...</span></div>';
                loader.style.position = 'absolute';
                loader.style.top = '100px';
                loader.style.left = '50%';
                loader.style.transform = 'translateX(-50%)';
                loader.style.zIndex = '10';

                instance.resultsContainer.appendChild(loader);
            }

            try {
                const response = await fetch(this.config.ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody)
                });

                if (response.ok) {
                    const html = await response.text();

                    // Parse temporarily to count items before rendering
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = html;
                    const returnedCount = tempDiv.querySelectorAll('.card').length;

                    // If the requested page has no items, abort and revert to current page
                    if (returnedCount === 0 && pageNumber > instance.currentPage) {
                        // Dynamically shrink totalPages since this page doesn't exist
                        instance.totalPages = Math.min(instance.totalPages, pageNumber - 1);
                        if (instance.currentPage >= instance.totalPages) {
                            instance.hasNext = false;
                        }
                        this.updatePaginationControls(instanceId, instance.currentPage, skipScroll);
                        return;
                    }

                    if (instance.resultsContainer) {
                        instance.resultsContainer.innerHTML = html;

                        tempDiv.querySelectorAll('.card[data-content-item-id]').forEach(card => {
                            instance.usedIds.add(card.getAttribute('data-content-item-id'));
                        });

                        // If we received fewer items than pageSize, or we reached max pages
                        if (returnedCount < instance.pageSize || pageNumber >= instance.totalPages) {
                            instance.hasNext = false;
                            instance.totalPages = pageNumber; // Lock the upper bound
                        } else {
                            instance.hasNext = true;
                        }
                    }
                    instance.currentPage = pageNumber;
                    this.updatePaginationControls(instanceId, pageNumber, skipScroll);
                } else {
                    console.error("Failed to fetch page", response.status);
                }
            } catch (error) {
                console.error("Error fetching pagination data", error);
            } finally {
                if (instance.resultsContainer) {
                    instance.resultsContainer.style.opacity = '1';
                    const loader = instance.resultsContainer.querySelector('.pagination-loader-overlay');
                    if (loader) loader.remove();
                }
            }
        },

        createPaginationControls(instance) {
            if (document.querySelector(`div[data-pagination-id="pagination-${instance.id}"]`)) return;

            const paginationContainer = document.createElement('div');
            paginationContainer.className = 'pagination-container justify-content-center mt-4';
            paginationContainer.setAttribute('data-pagination-id', `pagination-${instance.id}`);

            const paginationNav = document.createElement('nav');
            paginationNav.setAttribute('aria-label', 'Page navigation');

            const paginationList = document.createElement('ul');
            paginationList.className = 'pagination';

            // Previous button
            const prevLi = document.createElement('li');
            prevLi.className = 'page-item prev-button';
            prevLi.setAttribute('data-type', 'prev');
            const prevLink = document.createElement('button');
            prevLink.type = 'button';
            prevLink.className = 'page-link';
            prevLink.textContent = '‹';
            prevLink.addEventListener('click', e => {
                e.preventDefault();
                if (instance.currentPage > 1) this.fetchPage(instance.id, instance.currentPage - 1);
            });
            prevLi.appendChild(prevLink);
            paginationList.appendChild(prevLi);

            // Next button
            const nextLi = document.createElement('li');
            nextLi.className = 'page-item next-button';
            nextLi.setAttribute('data-type', 'next');
            const nextLink = document.createElement('button');
            nextLink.type = 'button';
            nextLink.className = 'page-link';
            nextLink.textContent = '›';
            nextLink.addEventListener('click', e => {
                e.preventDefault();
                if (instance.hasNext) this.fetchPage(instance.id, instance.currentPage + 1);
            });
            nextLi.appendChild(nextLink);
            paginationList.appendChild(nextLi);

            paginationNav.appendChild(paginationList);
            paginationContainer.appendChild(paginationNav);

            // Go to input
            const gotoContainer = document.createElement('div');
            gotoContainer.className = 'pagination-goto-container';

            const gotoLabel = document.createElement('label');
            gotoLabel.textContent = 'Go to:';
            gotoContainer.appendChild(gotoLabel);

            const gotoInput = document.createElement('input');
            gotoInput.type = 'number';
            gotoInput.min = '1';
            gotoInput.max = instance.totalPages;
            gotoInput.value = '1';
            gotoInput.placeholder = `e.g. 5`;
            gotoInput.setAttribute('data-pagination-id', instance.id);
            gotoInput.addEventListener('keypress', e => {
                if (e.key === 'Enter') this.handleGotoPage(gotoInput, instance);
            });
            gotoContainer.appendChild(gotoInput);
            paginationContainer.appendChild(gotoContainer);

            instance.listingElement.appendChild(paginationContainer);
        },

        handleGotoPage(inputElement, instance) {
            let pageNumber = parseInt(inputElement.value);
            if (isNaN(pageNumber) || pageNumber < 1) pageNumber = 1;
            if (pageNumber > instance.totalPages) pageNumber = instance.totalPages;
            inputElement.value = pageNumber;
            this.fetchPage(instance.id, pageNumber);
        },

        createPageNumbers(paginationList, instance) {
            const { currentPage, totalPages } = instance;
            const nextButton = paginationList.querySelector('.next-button');
            const delta = 1;
            const range = new Set();

            range.add(1);
            if (totalPages > 1) {
                range.add(totalPages);
            }

            for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) {
                range.add(i);
            }

            const pages = Array.from(range).sort((a, b) => a - b);

            let prev = null;
            for (const page of pages) {
                if (prev !== null && page - prev > 1) {
                    const dots = document.createElement('li');
                    dots.className = 'page-item disabled';
                    dots.innerHTML = '<span class="page-link">...</span>';
                    paginationList.insertBefore(dots, nextButton);
                }
                this.addPageNumberBefore(paginationList, page, instance, nextButton);
                prev = page;
            }
        },

        addPageNumberBefore(paginationList, pageNumber, instance, beforeElement) {
            const li = document.createElement('li');
            li.className = `page-item${pageNumber === instance.currentPage ? ' active' : ''}`;

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'page-link';
            btn.textContent = pageNumber;
            btn.setAttribute('data-page', pageNumber);
            btn.addEventListener('click', e => {
                e.preventDefault();
                this.fetchPage(instance.id, pageNumber);
            });

            li.appendChild(btn);
            paginationList.insertBefore(li, beforeElement);
        },

        updatePaginationControls(instanceId, currentPage, skipScroll = false) {
            const instance = this.instances.find(i => i.id === instanceId);
            if (!instance) return;

            const paginationContainer = document.querySelector(`div[data-pagination-id="pagination-${instanceId}"]`);
            if (!paginationContainer) return;

            const paginationList = paginationContainer.querySelector('ul.pagination');
            if (!paginationList) return;

            // Remove old page numbers, keep prev/next
            paginationList.querySelectorAll('li.page-item:not(.prev-button):not(.next-button)').forEach(li => li.remove());
            this.createPageNumbers(paginationList, instance);

            // Reset active/disabled states
            paginationList.querySelectorAll('.page-item').forEach(li => li.classList.remove('active', 'disabled'));

            const activeBtn = Array.from(paginationList.querySelectorAll('button[data-page]'))
                .find(btn => parseInt(btn.getAttribute('data-page')) === currentPage);
            if (activeBtn) activeBtn.parentElement.classList.add('active');

            const prevButton = paginationList.querySelector('.prev-button button');
            if (prevButton) {
                prevButton.disabled = currentPage === 1;
                prevButton.parentElement.classList.toggle('disabled', currentPage === 1);
            }

            const nextButton = paginationList.querySelector('.next-button button');
            if (nextButton) {
                nextButton.disabled = !instance.hasNext;
                nextButton.parentElement.classList.toggle('disabled', !instance.hasNext);
            }

            const gotoInput = paginationContainer.querySelector('.pagination-goto-container input');
            if (gotoInput) {
                gotoInput.value = currentPage;
                gotoInput.max = instance.totalPages;
            }

            if (!skipScroll) {
                window.scrollTo({
                    top: instance.listingElement.offsetTop - 100,
                    behavior: 'smooth'
                });
            }
        }
    };

    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', () => PaginationManager.init())
        : PaginationManager.init();

    window.PaginationManager = PaginationManager;
})();